import type { Dict } from '@aviata/platform-types';

import { appLocale } from '../../shared/model';
import { searchCitiesConfig } from './config';
import {
  LocationType,
  type MappedCity,
  type MappedAirport,
  type MappedCountry,
} from '../../shared/types';

const locale = appLocale.lib.getAppLocale();

type Location =
  | Dict.Cities.City
  | Dict.Cities.City['country']
  | Dict.Airport
  | MappedCity
  | MappedCountry
  | MappedAirport
  | MappedAirport['city'];

const getLocalizedName = (location: Location) => {
  return location[`name_${locale}`];
};

const getLocationCode = (location: Location): string => {
  const { code, iata, iso_code } = location as Partial<
    Record<'code' | 'iata' | 'iso_code', string>
  >;
  return code ?? iata ?? iso_code ?? '';
};

function localizeCountry(country: MappedCountry): MappedCountry {
  return {
    ...country,
    name: getLocalizedName(country),
  };
}

function localizeCity(city: MappedCity | MappedAirport['city']) {
  return {
    ...city,
    country:
      'country' in city && city.country
        ? localizeCountry(city.country)
        : undefined,
    name: getLocalizedName(city),
    children:
      'children' in city
        ? city.children?.map(child => localizeAirport(child))
        : undefined,
  };
}

function localizeAirport(airport: MappedAirport): MappedAirport {
  return {
    ...airport,
    city: localizeCity(airport.city),
    country: localizeCountry(airport.country),
    name: getLocalizedName(airport),
  };
}

function localizeLocation<T extends MappedCity | MappedAirport | MappedCountry>(
  location: T,
): T;
function localizeLocation(
  location: MappedCity | MappedAirport | MappedCountry,
) {
  if (location.type === LocationType.AIRPORT) {
    return localizeAirport(location);
  }
  if (location.type === LocationType.COUNTRY) {
    return localizeCountry(location);
  }
  return localizeCity(location);
}

type MappedLocationResult<T extends LocationType> = {
  name: string;
  name_ru: string;
  name_kk: string;
  name_en: string;
  code: string;
  type: T;
};

export function mapLocation<T extends LocationType>(
  location: Location,
  type: T,
): MappedLocationResult<T>;
export function mapLocation<T extends LocationType>(
  location: Location | null | undefined,
  type: T,
): MappedLocationResult<T> | null;
export function mapLocation<T extends LocationType>(
  location: Location | null | undefined,
  type: T,
): MappedLocationResult<T> | null {
  if (!location) {
    return null;
  }

  return {
    name: getLocalizedName(location),
    name_ru: location.name_ru,
    name_kk: location.name_kk,
    name_en: location.name_en,
    code: getLocationCode(location),
    type,
  };
}

const getMappedAirports = (city: Dict.Cities.City): Array<MappedAirport> => {
  return city.airports.map(airport => ({
    ...mapLocation(airport, LocationType.AIRPORT),
    codeForBackend: `${city.iata}${airport.iata}`,
    city: mapLocation(city, LocationType.CITY),
    country: mapLocation(city.country, LocationType.COUNTRY),
  }));
};

const getMappedCities = (
  cities: Array<Dict.Cities.City | MappedCity>,
): Array<MappedCity> => {
  return cities.map(city => ({
    ...mapLocation(city, LocationType.CITY),
    country: mapLocation(city.country, LocationType.COUNTRY),
    children:
      'airports' in city && city.airports.length > 1
        ? getMappedAirports(city)
        : [],
  }));
};

function getArrivalPopularRoutes() {
  // only needed for localization
  return getMappedCities(searchCitiesConfig.POPULAR_ARRIVALS);
}

function getDeparturePopularRoutes() {
  // only needed for localization
  return getMappedCities(searchCitiesConfig.POPULAR_DEPARTURES);
}

export const citySearchLibs = {
  getArrivalPopularRoutes,
  getDeparturePopularRoutes,
  getMappedCities,
  localizeLocation,
};
